import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import PropTypes from "prop-types";

import Popper from "@mui/material/Popper";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

import "./Sidenav.css";

import vortex_logo from "../../assets/images/vortex_logo.png";
import vortex_logo_min from "../../assets/images/vortex_logo_min.png";

const useStyles = makeStyles()(() => ({
  img: { width: "150px" },
  img_min: { width: "30px" },
  navLink: {
    color: "var(--white)",
    textDecoration: "none",
    padding: "8px 16px",
    display: "flex",
    alignItems: "center",
    transition: "all 0.3s ease",
    "&.active": { background: "#32343C" },
    "&:hover": {
      color: "var(--orange)",
    },
  },
  drawer: {
    background: "#3a3d4a",
    color: "#ffffff",
    height: "100vh !important",
  },
}));

// Recursively checks item + children for an active match
const isPathActive = (item, path) => {
  if (path.startsWith(item.navigateTo)) return true;
  if (item.children) return item.children.some((child) => isPathActive(child, path));
  return false;
};

const KMKSidenav = ({ sidenavItems, navBottomItems }) => {
  const {
    sideNavbar: { open },
  } = useSelector((s) => s);

  const location = useLocation();
  const selectedPath = useMemo(() => location.pathname, [location.pathname]);

  const { classes } = useStyles();
  const theme = useTheme();

  const [expandedItems, setExpandedItems] = useState({});

  // Popper submenu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredChildren, setHoveredChildren] = useState([]);
  const [popperOpen, setPopperOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleMouseEnterTrigger = useCallback((e, children) => {
    clearTimeout(closeTimer.current);
    setAnchorEl(e.currentTarget);
    setHoveredChildren(children);
    setPopperOpen(true);
  }, []);

  const handleMouseLeaveBoth = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setPopperOpen(false);
    }, 200);
  }, []);

  const handleItemClick = useCallback(
    (e, item) => {
      if (item.children) {
        e.preventDefault();
        if (open)
          setExpandedItems((prev) => ({
            ...prev,
            [item.id]: !prev[item.id],
          }));
      }
    },
    [open]
  );

  const renderItems = useCallback(
    (item, child = false) => {
      const active = isPathActive(item, selectedPath);

      return (
        <Box key={item.id}>
          <NavLink
            to={item.navigateTo}
            className={classes.navLink}
            style={{
              ...(!open ? { justifyContent: "center", padding: 0 } : {}),
              ...(active ? { borderRight: "4px solid var(--orange)" } : {}),
            }}
            download={item.download ? "Vortex Guide.pdf" : undefined}
            target={item.download ? "_blank" : undefined}
            onClick={(e) => handleItemClick(e, item)}
            onMouseEnter={(e) => {
              if (!open && item.children) handleMouseEnterTrigger(e, item.children);
            }}
            onMouseLeave={() => {
              if (!open && item.children) handleMouseLeaveBoth();
            }}
          >
            {open ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  pl: child ? 3 : 1.5,
                  py: child ? 0.75 : 1.25,
                  fontSize: child ? "0.875rem" : "1rem",
                  height: child ? "36px" : "44px",
                }}
              >
                <Box>{item.icon || item.image}</Box>
                <Box sx={{ ml: 2, flexGrow: 1 }}>{item.title}</Box>
                {item.children &&
                  (expandedItems[item.id] ? <ExpandMoreRoundedIcon /> : <KeyboardArrowRightRoundedIcon />)}
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: child ? 0.75 : 1.25 }}>
                {item.icon || item.image}
                <Box sx={{ fontSize: 10 }}>{item.title}</Box>
              </Box>
            )}
          </NavLink>

          <Collapse in={!!expandedItems[item.id]} timeout="auto" unmountOnExit>
            {item.children?.map((c) => renderItems(c, true))}
          </Collapse>
        </Box>
      );
    },
    [classes.navLink, expandedItems, handleItemClick, handleMouseEnterTrigger, handleMouseLeaveBoth, open, selectedPath]
  );

  useEffect(() => {
    if (!open) setExpandedItems({});
  }, [open]);

  return (
    <Box sx={{ display: "flex", height: "100vh", width: open ? 240 : 80 }}>
      <Drawer
        variant="permanent"
        open={open}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            width: open ? 240 : 80,
            height: "100vh",
            bgcolor: "#3a3d4a",
            color: "#ffffff",
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { background: "#555" },
          },
        }}
      >
        <Box sx={{ flexShrink: 0, textAlign: "center", my: 2 }}>
          {open ? (
            <img src={vortex_logo} className={classes.img} alt="logo" />
          ) : (
            <img src={vortex_logo_min} className={classes.img_min} alt="logo" />
          )}
        </Box>

        <Box>{sidenavItems.map((item) => renderItems(item))}</Box>

        {navBottomItems?.length > 0 && (
          <Box
            sx={{
              mt: "auto",
              flexShrink: 0,
              borderTop: "1px solid rgba(255,255,255,0.1)",
              py: 1,
              bgcolor: "#3a3d4a",
            }}
          >
            {navBottomItems.map((item) => renderItems(item, true))}
          </Box>
        )}
      </Drawer>

      <Popper
        open={popperOpen}
        anchorEl={anchorEl}
        placement="right-start"
        disablePortal
        onMouseEnter={() => clearTimeout(closeTimer.current)}
        onMouseLeave={handleMouseLeaveBoth}
        modifiers={[{ name: "offset", options: { offset: [4, 8] } }]}
        sx={{
          borderRadius: 1,
          boxShadow: 3,
          zIndex: theme.zIndex.drawer + 1,
          minWidth: "200px",
          background: "#3a3d4a",
          "&.active": { background: "#32343C" },
        }}
      >
        <Box sx={{ py: 1 }}>
          {hoveredChildren.map((child) => {
            const childActive = isPathActive(child, selectedPath);
            return (
              <NavLink
                key={child.id}
                to={child.navigateTo}
                className={classes.navLink}
                style={{
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  ...(childActive ? { borderRight: "4px solid var(--orange)" } : {}),
                }}
                onClick={() => setPopperOpen(false)}
              >
                {child.icon || child.image}
                <Box sx={{ ml: 1 }}>{child.title}</Box>
              </NavLink>
            );
          })}
        </Box>
      </Popper>
    </Box>
  );
};

KMKSidenav.propTypes = {
  sidenavItems: PropTypes.array.isRequired,
  navBottomItems: PropTypes.array,
};

export default KMKSidenav;
